import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import { useSeo } from "@/hooks/useSeo";
import {
  getAllVideos,
  getCategoryInfo,
  paginate,
  queryVideos,
} from "@/lib/videoService";
import FilterBar from "./components/FilterBar";

export default function Category() {
  const { type } = useParams();
  const [params, setParams] = useSearchParams();

  const info = getCategoryInfo(type);
  const categoryName = info?.name || decodeURIComponent(type || "");

  useSeo({
    title: `芭乐视频 - ${categoryName}频道`,
    description:
      info?.desc ||
      `${categoryName}频道汇聚芭乐视频站内${categoryName}资源，支持按地域、年份与题材筛选，快速发现优质内容。`,
    keywords: `芭乐视频,${categoryName},高清${categoryName},在线观看,影视平台`,
  });

  const region = params.get("region") || "全部";
  const year = params.get("year") || "全部";
  const genre = params.get("genre") || "全部";
  const sort = (params.get("sort") as "latest" | "hot" | "rating") || "latest";
  const page = Number(params.get("page") || 1);

  const typeVideos = useMemo(
    () => getAllVideos().filter((v) => v.type === categoryName),
    [categoryName]
  );

  const regionOpts = useMemo(() => {
    const set = Array.from(new Set(typeVideos.map((v) => v.region)));
    return ["全部", ...set];
  }, [typeVideos]);

  const yearOpts = useMemo(() => {
    const set = Array.from(new Set(typeVideos.map((v) => String(v.year)))).sort((a, b) => Number(b) - Number(a));
    return ["全部", ...set];
  }, [typeVideos]);

  const genreOpts = useMemo(() => {
    const set = new Set<string>();
    typeVideos.forEach((v) => v.genre.forEach((g) => set.add(g)));
    return ["全部", ...Array.from(set)];
  }, [typeVideos]);

  const result = useMemo(
    () => queryVideos({ type: categoryName, region, year, genre, sort }),
    [categoryName, region, year, genre, sort]
  );

  const { items, total, totalPages, currentPage } = paginate(result, page);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === "全部") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  };

  const changePage = (nextPage: number) => {
    const next = new URLSearchParams(params);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetFilters = () => setParams(new URLSearchParams(), { replace: true });

  const hasFilter = region !== "全部" || year !== "全部" || genre !== "全部";

  const groups = [
    { key: "region" as const, label: "地域", options: regionOpts },
    { key: "year" as const, label: "年份", options: yearOpts },
    { key: "genre" as const, label: "题材", options: genreOpts },
  ];

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-5" aria-label="面包屑导航">
        <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-300" />
        <span className="text-foreground-800 font-medium">{categoryName}</span>
      </nav>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            {categoryName}频道
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            {info?.desc || `精彩${categoryName}内容持续更新，欢迎浏览。`}
          </p>
        </div>
        <p className="text-sm text-foreground-500 whitespace-nowrap">
          共 <span className="font-label font-semibold text-primary-600">{total}</span> 部作品
        </p>
      </div>

      <FilterBar
        groups={groups}
        values={{ region, year, genre }}
        onChange={updateParam}
        sort={sort}
        onSortChange={(value) => updateParam("sort", value)}
      />

      {items.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5 mt-7">
            {items.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
          <div className="mt-9">
            <Pagination currentPage={currentPage} totalPages={totalPages} onSelect={changePage} />
          </div>
        </>
      ) : (
        <div className="mt-7 rounded-lg bg-background-100 border border-background-200/70 py-16 flex flex-col items-center text-center px-4">
          <span className="flex items-center justify-center w-16 h-16 rounded-full bg-background-200 text-foreground-400">
            <i className="ri-search-eye-line text-3xl" />
          </span>
          <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
            没有找到符合条件的内容
          </h3>
          <p className="mt-2 text-sm text-foreground-500 max-w-md">
            试试减少筛选条件，或重置后浏览该频道的全部作品。
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {hasFilter ? (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
              >
                <i className="ri-refresh-line" /> 重置筛选
              </button>
            ) : null}
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 whitespace-nowrap hover:bg-background-200 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}